import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  type SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import useSWR from "swr";
import { swrKey, usersFetcher, type SearchType } from "components/usersApi";
import { getApprovalDisplayName, getJaDisplayName } from "components/userUtil.ts";

const SEARCH_TYPE_SELECT: { value: SearchType, label: string }[] = [
  { value: "exact", label: "完全一致" },
  { value: "prefix", label: "前方一致" },
]

const SEARCH_FIELDS: string[] = [
  "memberNumber",
  "applicantName",
  "email",
  "companyName",
]

export default function Users() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchField, setSearchField] = useState("email");
  const [searchType, setSearchType] = useState<SearchType>("exact");

  // Active search parameters that are only updated when search button is clicked
  const [activeSearchTerm, setActiveSearchTerm] = useState<string | null>(null);
  const [activeSearchField, setActiveSearchField] = useState<string | null>(null);
  const [activeSearchType, setActiveSearchType] = useState<SearchType | null>(null);

  const { data, error, isLoading } = useSWR(
    [swrKey, page, rowsPerPage, activeSearchTerm, activeSearchField, activeSearchType],
    usersFetcher,
  );

  // Initialize parameters from URL query parameters
  useEffect(() => {
    const pageParam = searchParams.get("page");
    if (pageParam) {
      setPage(parseInt(pageParam, 10));
    }

    const searchTermParam = searchParams.get("searchTerm");
    if (searchTermParam) {
      setSearchTerm(searchTermParam);
      setActiveSearchTerm(searchTermParam);
    }

    const searchFieldParam = searchParams.get("searchField");
    if (searchFieldParam && SEARCH_FIELDS.includes(searchFieldParam)) {
      setSearchField(searchFieldParam);
      setActiveSearchField(searchFieldParam);
    }

    const searchTypeParam = searchParams.get("searchType") as SearchType;
    if (searchTypeParam && (searchTypeParam === "exact" || searchTypeParam === "prefix")) {
      setSearchType(searchTypeParam);
      setActiveSearchType(searchTypeParam);
    }
  }, [searchParams]);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    // Update URL query parameter
    searchParams.set("page", newPage.toString());
    setSearchParams(searchParams);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const newRowsPerPage = parseInt(event.target.value, 10);
    setRowsPerPage(newRowsPerPage);
    setPage(0);
    // Update URL query parameter
    searchParams.set("page", "0");
    setSearchParams(searchParams);
  };

  const handleSearchFieldChange = (event: SelectChangeEvent) => {
    setSearchField(event.target.value);
  };

  const handleSearchTypeChange = (event: SelectChangeEvent) => {
    setSearchType(event.target.value as SearchType);
  };

  const handleSearchTermChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setSearchTerm(event.target.value);
  };

  const handleSearch = () => {
    // Reset to first page when searching
    setPage(0);

    // Update URL query parameters
    searchParams.set("page", "0");

    if (searchTerm) {
      searchParams.set("searchTerm", searchTerm);
      searchParams.set("searchField", searchField);
      searchParams.set("searchType", searchType);

      // Update active search parameters
      setActiveSearchTerm(searchTerm);
      setActiveSearchField(searchField);
      setActiveSearchType(searchType);
    } else {
      // If search term is empty, remove search parameters from URL
      searchParams.delete("searchTerm");
      searchParams.delete("searchField");
      searchParams.delete("searchType");

      // Clear active search parameters
      setActiveSearchTerm(null);
      setActiveSearchField(null);
      setActiveSearchType(null);
    }

    setSearchParams(searchParams);
  };

  const handleSearchClear = () => {
    // Clear search input fields
    setSearchTerm("");
    setSearchField("email");
    setSearchType("exact");

    // Clear active search parameters
    setActiveSearchTerm(null);
    setActiveSearchField(null);
    setActiveSearchType(null);

    // Reset to first page
    setPage(0);

    // Update URL by removing search parameters
    searchParams.delete("searchTerm");
    searchParams.delete("searchField");
    searchParams.delete("searchType");
    searchParams.set("page", "0");
    setSearchParams(searchParams);
  }

  const handleDetailsClick = (userId: string) => {
    // Preserve the current page in the URL when navigating to details
    navigate(`/users/${userId}/details?returnPage=${page}`);
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom>
        会員一覧
      </Typography>

      {/* Search Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, sm:"grow" }} sx={{ flexGrow: 1 }}>
            <TextField
              fullWidth
              label="検索キーワード"
              variant="outlined"
              value={searchTerm}
              onChange={handleSearchTermChange}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: "auto" }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id="search-field-label">検索項目</InputLabel>
              <Select
                labelId="search-field-label"
                id="search-field"
                value={searchField}
                label="検索項目"
                onChange={handleSearchFieldChange}
              >
                {SEARCH_FIELDS.map((s) => (
                  <MenuItem key={s} value={s}>{getJaDisplayName(s)}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: "auto" }}>
            <FormControl sx={{ minWidth: 120 }}>
              <InputLabel id="search-type-label">検索タイプ</InputLabel>
              <Select
                labelId="search-type-label"
                id="search-type"
                value={searchType}
                label="検索タイプ"
                onChange={handleSearchTypeChange}
              >
                {SEARCH_TYPE_SELECT.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, sm: "auto" }}>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={!searchTerm}
            >
              検索
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: "auto" }}>
            <Button
              variant="outlined"
              color="secondary"
              onClick={handleSearchClear}
              disabled={!activeSearchTerm}
            >
              クリア
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 650 }} aria-label="users table">
          <TableHead>
            <TableRow>
              <TableCell style={{ minWidth: 180 }}>{getJaDisplayName("email")}</TableCell>
              <TableCell style={{ minWidth: 140 }}>{getJaDisplayName("memberNumber")}</TableCell>
              <TableCell style={{ minWidth: 100 }}>{getJaDisplayName("companyName")}</TableCell>
              <TableCell style={{ minWidth: 150 }}>
                <div>{getJaDisplayName("applicantName")}</div>
                <div>{getJaDisplayName("applicantNameKana")}</div>
              </TableCell>
              <TableCell style={{ minWidth: 100 }}>会員状況<br />詳細登録</TableCell>
              <TableCell align="center">詳細</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box
                    sx={{ display: "flex", justifyContent: "center", mt: 4 }}
                  >
                    <CircularProgress />
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {error && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Box sx={{ mt: 2, mb: 2, color: "error.main" }}>
                    <Typography variant="h6" color="error">
                      エラーが発生しました
                    </Typography>
                    <Typography variant="body2">
                      {error.message || "データの取得中に問題が発生しました。"}
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {!isLoading &&
              !error &&
              data &&
              data.users.map((user) => (
                <TableRow key={user.id}>
                  <TableCell style={{ wordBreak: "break-all" }}>{user.email}</TableCell>
                  <TableCell style={{ wordBreak: "break-all" }}>{user.memberNumber}</TableCell>
                  <TableCell style={{ wordBreak: "break-all" }}>{user.companyName}</TableCell>
                  <TableCell>
                    <div style={{ wordBreak: "break-all" }}>{user.applicantName}</div>
                    <div style={{ wordBreak: "break-all" }}>{user.applicantNameKana}</div>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography
                        component="span"
                        variant="body2"
                        color={user.isApproved ? 'primary' : 'textSecondary'}
                      >
                        {getApprovalDisplayName(user.isApproved)}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography
                        component="span"
                        variant="body2"
                        color={user.isDetailRegistered ? 'primary' : 'textSecondary'}
                      >
                        {user.isDetailRegistered ? '入力済' : '未入力'}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => handleDetailsClick(user.id)}
                    >
                      詳細
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            {!isLoading && !error && (!data || data.users.length === 0) && (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography variant="body1" sx={{ py: 2 }}>
                    データがありません
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          rowsPerPageOptions={[10]}
          component="div"
          count={data?.totalCount ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="表示件数:"
        />
      </TableContainer>
    </Container>
  );
}
